import React from 'react';
import { useTranslation } from 'react-i18next';

import { captureException } from '@sentry/browser';

import { FitnessApplicationSettingsInput } from '../../../../types/__generated__';
import useSaveStravaSettings from '../../../hooks/fitness/useSaveStravaSettings';
import ConfirmationModal from '../../ConfirmationModal/ConfirmationModal';
import { createStravaInput } from '../StravaSettingsModal/StravaSettingsModal';
import { pausedStravaSettings } from '../StravaSettingsModal/StravaSettingsModalConsts';
import { ModalType } from '../StravaModal';
import { useApp } from '../../../hooks/useApp';
import useBasePageQuery from '../../../hooks/useBasePageQuery';
import styles from './StravaStopSharingConfirmation.scss';
import config from '../../../client/config';

interface ConfirmationModalProps {
  onClose: () => void;
  onModalTypeChange: (type: ModalType) => void;
}

export const StravaStopSharingConfirmation = React.forwardRef(
  (
    { onModalTypeChange, onClose }: ConfirmationModalProps,
    ref?: React.Ref<HTMLDivElement>,
  ) => {
    const { t } = useTranslation('common', { useSuspense: false });

    const [setFitnessApplicationSettings] = useSaveStravaSettings();

    const { data } = useBasePageQuery();
    const { showToast } = useApp();

    const pauseStrava = async () => {
      const input: FitnessApplicationSettingsInput = createStravaInput(
        pausedStravaSettings,
        data?.page?.id ?? '',
      );

      try {
        await setFitnessApplicationSettings(input);
        showToast({
          message: t('strava.stopSharing.success'),
          type: 'success',
          dismissAfter: 5000,
        });
      } catch (error) {
        showToast({
          message: t('strava.stopSharing.warning'),
          type: 'warning',
          dismissAfter: 10000,
        });
        captureException(error);
      }
      onClose();
    };

    const pageViewTrackingData = {
      event: 'page view',
      subtype: 'stop sharing confirmation',
      page_section: 'strava -> manage strava settings',
    };

    return (
      <ConfirmationModal
        modalTitle={t('strava.confirmationModal.title')}
        confirmButtonText={t('strava.stopSharing.confirmButton')}
        dismissButtonText={t('strava.stopSharing.cancelButton')}
        ref={ref}
        onDismiss={() => onModalTypeChange('settings')}
        onConfirm={pauseStrava}
        pageViewTrackingData={pageViewTrackingData}
      >
        <div className={styles.description}>
          {t('strava.confirmationModal.stopSharingModalDesc-1')}
        </div>
        <div className={styles.description}>
          {t('strava.confirmationModal.stopSharingModalDesc-2')}
        </div>

        <div className={styles.description}>
          {t('strava.confirmationModal.stopSharingModalDesc-3a')}
          <a href={`${config.baseUri}/user-account/apps`}>
            {t('strava.confirmationModal.stopSharingModalDesc-3b')}
          </a>
        </div>
      </ConfirmationModal>
    );
  },
);

StravaStopSharingConfirmation.displayName = 'StravaStopSharingConfirmation';
